import { useForm, useWatch } from "react-hook-form";
import styled from "styled-components";
import { FC, FormEvent, MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import {
  RadioCircle,
  RadioInput,
  Radios,
  RadiosLabel,
  Textarea,
} from "@/components/Forms";
import FormField from "@/components/Forms/FormField";
import { api } from "@/utils/api";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { ImageGenerationProjectResource } from "@/types/resources";

interface FormData {
  name: string;
  description: string;
  should_regenerate_queries: boolean;
}

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  padding: 24px;
`;

const StyledTextArea = styled(Textarea)`
  border-radius: 4px;
  min-height: 344px;
  padding: 24px 16px;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  resize: none;
  margin-top: 24px;
  &::placeholder {
    font-weight: 600;
  }
`;

const StyledRadios = styled(Radios)`
  margin-top: 24px;
`;

const GenerateButton = styled(Button)`
  padding: 0 32px;
`;

const ButtonHolder = styled.div`
  display: flex;
  margin-top: 32px;
  margin-bottom: 56px;
  gap: 16px;
  justify-content: flex-end;
`;

const Header = styled.div`
  display: flex;
  div {
    &:first-child {
      flex: 1;
    }
  }
`;

const HeaderButtons = styled.div`
  margin-left: 165px;
  display: flex;
  gap: 16px;
`;

const DoneButton = styled(Button)`
  font-size: 14px;
  line-height: 22px;
  height: 38px;
  padding: 0 32px;
`;

const CancelButton = styled(DoneButton)`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

interface Props {
  project?: ImageGenerationProjectResource;
  hideForm?: () => void;
}

const ImageToolMainForm: FC<Props> = ({ project, hideForm }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    register,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<FormData>({
    defaultValues: {
      name: project?.name ?? "",
      description: project?.description ?? "",
      should_regenerate_queries: true,
    },
  });

  const shouldGenerate = useWatch({
    control,
    name: "should_regenerate_queries",
  });

  const descriptionValue = useWatch({
    control,
    name: "description",
  });

  const onSubmit = (
    e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>
  ) => {
    clearErrors();
    return handleSubmit(
      async ({ name, description, should_regenerate_queries }) => {
        try {
          if (project && hideForm) {
            await api.put<FormData, { id: number }>(
              `/v1/image-generation-projects/${project.id}`,
              {
                name,
                should_regenerate_queries,
                ...(!!description.length && {
                  description,
                }),
              }
            );

            await queryClient.invalidateQueries(["image_generation_project"], {
              exact: false,
            });

            queryClient.invalidateQueries(["image_generation_projects"], {
              exact: false,
            });

            hideForm();
          } else {
            const { id } = await api.post<FormData, { id: number }>(
              "/v1/image-generation-projects",
              {
                name,
                should_regenerate_queries,
                ...(!!description.length && {
                  description,
                }),
              }
            );

            await queryClient.invalidateQueries(["image_generation_projects"], {
              exact: false,
            });

            navigate(`/general/image-tool/${id}`);
          }
        } catch (err) {
          handleFormErrors<FormData>(err, setError, alerts);
        }
      }
    )(e);
  };

  return (
    <form
      onSubmit={(e) => {
        onSubmit(e);
      }}
      autoComplete="off"
    >
      <Box>
        <Header>
          <FormField
            placeholder="Add name"
            label="Project Name"
            name="name"
            control={control}
          />
          {project && (
            <HeaderButtons>
              <DoneButton
                color="colorized"
                type="button"
                disabled={isSubmitting}
                loading={isSubmitting && !shouldGenerate}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  setValue("should_regenerate_queries", false);
                  onSubmit(e);
                }}
              >
                Done
              </DoneButton>
              <CancelButton color="secondary" type="button" onClick={hideForm}>
                Cancel
              </CancelButton>
            </HeaderButtons>
          )}
        </Header>
        <StyledRadios>
          <RadiosLabel>
            Text analysis with ChatGPT
            <RadioInput
              disabled={!!project}
              type="radio"
              name="selection"
              checked={!project ? shouldGenerate : false}
              onChange={() => setValue("should_regenerate_queries", true)}
            />
            <RadioCircle />
          </RadiosLabel>
          <RadiosLabel>
            Custom search queries & prompts
            <RadioInput
              disabled={!!project}
              type="radio"
              name="selection"
              checked={!project ? !shouldGenerate : false}
              onChange={() => setValue("should_regenerate_queries", false)}
            />
            <RadioCircle />
          </RadiosLabel>
        </StyledRadios>
        {shouldGenerate && (
          <>
            <StyledTextArea
              {...register("description")}
              placeholder="Add text here (article, video script or description)"
              $hasError={!!errors.description}
            ></StyledTextArea>
            {!!errors.description && (
              <ValidationMessage>
                {errors.description.message as string}
              </ValidationMessage>
            )}
          </>
        )}
      </Box>
      <ButtonHolder>
        {!!project ? (
          <GenerateButton
            disabled={isSubmitting || !descriptionValue}
            loading={isSubmitting && shouldGenerate}
          >
            Regenerate request
          </GenerateButton>
        ) : shouldGenerate ? (
          <GenerateButton
            disabled={isSubmitting || !descriptionValue}
            loading={isSubmitting}
          >
            Generate request
          </GenerateButton>
        ) : (
          <GenerateButton disabled={isSubmitting} loading={isSubmitting}>
            Start
          </GenerateButton>
        )}
      </ButtonHolder>
    </form>
  );
};

export default ImageToolMainForm;
